import { fromDateOnlyString } from "@/lib/format-data";

/**
 * Shared helpers for document (ID) expiry status. An ID is "expiring soon" when
 * it expires within this many days.
 */
export const ID_EXPIRING_SOON_DAYS = 90;

export type ExpiryStatus = "expired" | "expiring" | "valid";

export interface ExpiryInfo {
  status: ExpiryStatus;
  /** Whole days until expiry (negative if already expired). */
  daysUntil: number;
  /** Short human label, e.g. "Expired", "Expires in 42 days". */
  label: string;
  /** Maps to the app's status badge variants. */
  badgeVariant: "error" | "warning" | "success";
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Compute expiry status for a date-only string ("YYYY-MM-DD", or the date part
 * of an ISO datetime). Returns null when there's no expiry on file. The date is
 * parsed as a *calendar* date (via `fromDateOnlyString`) rather than
 * `new Date(...)` — the latter reads UTC-midnight values in local time and
 * shifts the day for users west of UTC. Comparison is date-only so "expires
 * today" reads as 0 days.
 */
export function getExpiryInfo(
  expiryDate: string | null | undefined,
): ExpiryInfo | null {
  if (!expiryDate) return null;
  const startOfExpiry = fromDateOnlyString(expiryDate.slice(0, 10));
  if (isNaN(startOfExpiry.getTime())) return null;
  // fromDateOnlyString returns noon; floor to midnight so the delta below is a
  // whole number of days and Math.round doesn't shift by the half-day offset.
  startOfExpiry.setHours(0, 0, 0, 0);

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const daysUntil = Math.round(
    (startOfExpiry.getTime() - startOfToday.getTime()) / MS_PER_DAY,
  );

  if (daysUntil < 0) {
    return {
      status: "expired",
      daysUntil,
      label: "Expired",
      badgeVariant: "error",
    };
  }
  if (daysUntil <= ID_EXPIRING_SOON_DAYS) {
    return {
      status: "expiring",
      daysUntil,
      label:
        daysUntil === 0
          ? "Expires today"
          : `Expires in ${daysUntil} day${daysUntil === 1 ? "" : "s"}`,
      badgeVariant: "warning",
    };
  }
  return {
    status: "valid",
    daysUntil,
    label: "Valid",
    badgeVariant: "success",
  };
}
